import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Row, Col, Card, Typography, Select, Space, Button, Spin, DatePicker } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import iconReport from '../../icons/report.png';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LabelList, ReferenceLine,
} from 'recharts';
import {
  getProfitLossReportApi,
  getExpenseBreakdownReportApi,
  getTimelineReportApi,
  getYearOverYearReportApi,
  getPlotPerformanceReportApi,
} from '../../api/reports';
import { getFarmListApi } from '../../api/crops';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const C = {
  expense: '#fa8c16',
  income:  '#52c41a',
  profit:  '#1a6b2a',
  loss:    '#ff4d4f',
  pie: ['#1a6b2a', '#52c41a', '#fa8c16', '#1677ff', '#722ed1', '#eb2f96'],
  yoy: ['#1a6b2a', '#52c41a', '#1677ff', '#fa8c16', '#722ed1'],
};

const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;
const shortInr = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (Math.abs(n) >= 1000)   return `₹${(n / 1000).toFixed(0)}k`;
  return `₹${n}`;
};
const fmtMonth = (m) => (m ? dayjs(m + '-01').format("MMM 'YY") : '');

function transformYoY(apiResponse) {
  const crops = apiResponse?.data ?? [];
  if (!crops.length) return { years: [], data: [] };
  const allYears = [...new Set(crops.flatMap(c => c.years.map(y => String(y.year))))].sort();
  const data = crops.map(crop => {
    const row = { crop: crop.crop_name };
    crop.years.forEach(y => { row[String(y.year)] = y.profit; });
    return row;
  });
  return { years: allYears, data };
}

function ChartCard({ title, subtitle, extra, children, loading, minHeight = 300 }) {
  return (
    <Card
      title={<span style={{ fontWeight: 700, fontSize: 14 }}>{title}</span>}
      extra={
        <Space size={8} wrap>
          {subtitle && <Text type="secondary" style={{ fontSize: 12 }}>{subtitle}</Text>}
          {extra}
        </Space>
      }
      style={{ borderRadius: 14, height: '100%' }}
      styles={{ body: { padding: '4px 16px 16px' } }}
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: minHeight }}>
          <Spin />
        </div>
      ) : children}
    </Card>
  );
}

const CustomTooltip = ({ active, payload, label, labelFormatter }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #f0f0f0', borderRadius: 8, padding: '8px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 13 }}>{labelFormatter ? labelFormatter(label) : label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ fontSize: 12, color: p.color, display: 'flex', justifyContent: 'space-between', gap: 16 }}>
          <span>{p.name}</span>
          <span style={{ fontWeight: 600 }}>{inr(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

const PAYMENT_STATUS_OPTIONS = [
  { value: 'paid',         label: 'Paid' },
  { value: 'unpaid',       label: 'Unpaid' },
  { value: 'partial_paid', label: 'Partial Paid' },
  { value: 'na',           label: 'N/A' },
];

const ACCOUNT_LINK_OPTIONS = [
  { value: 'labour',      label: 'Labour' },
  { value: 'shop_keeper', label: 'Shop Keeper' },
];

export default function Reports() {
  const [dateRange,  setDateRange]  = useState([dayjs().subtract(11, 'month').startOf('month'), dayjs().startOf('month')]);
  const [expFilters, setExpFilters] = useState({ payment_status: undefined, account_link: undefined, khetar_id: undefined });
  const [farmOptions, setFarmOptions] = useState([]);

  const [cropProfit, setCropProfit] = useState([]);
  const [expBreak,   setExpBreak]   = useState([]);
  const [cashflow,   setCashflow]   = useState([]);
  const [yoyData,    setYoyData]    = useState({ years: [], data: [] });
  const [plotPerf,   setPlotPerf]   = useState([]);

  const [busy, setBusy] = useState({});

  useEffect(() => {
    getFarmListApi()
      .then(res => {
        const list = res.data?.data ?? res.data ?? [];
        setFarmOptions(list.map(f => ({ value: f.id, label: f.name || f.farm_name })));
      })
      .catch(() => {});
  }, []);

  const go = useCallback(async (key, apiFn, setter) => {
    setBusy(b => ({ ...b, [key]: true }));
    try {
      const res = await apiFn();
      setter(res.data);
    } catch {} finally {
      setBusy(b => ({ ...b, [key]: false }));
    }
  }, []);

  const fetchAll = useCallback(() => {
    const dp = {
      date_from: dateRange ? dateRange[0].format('YYYY-MM') : undefined,
      date_to:   dateRange ? dateRange[1].format('YYYY-MM') : undefined,
    };

    go('crop', () => getProfitLossReportApi(dp), data => {
      setCropProfit(data?.data ?? []);
    });

    go('exp', () => getExpenseBreakdownReportApi({ ...dp, ...expFilters }), data => {
      setExpBreak((data?.by_category ?? []).map(c => ({ ...c, amount: Number(c.total_amount) || 0 })));
    });

    go('flow', () => getTimelineReportApi(dp), data => {
      setCashflow(data?.data ?? []);
    });

    go('plot', () => getPlotPerformanceReportApi(dp), data => {
      setPlotPerf(data?.data ?? []);
    });

    setBusy(b => ({ ...b, yoy: true }));
    getYearOverYearReportApi()
      .then(res => setYoyData(transformYoY(res.data)))
      .catch(() => {})
      .finally(() => setBusy(b => ({ ...b, yoy: false })));
  }, [dateRange, expFilters, go]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const PIE_TOP = 7;
  const expSorted  = useMemo(() => [...expBreak].sort((a, b) => b.amount - a.amount), [expBreak]);
  const expTotal   = useMemo(() => expBreak.reduce((s, c) => s + c.amount, 0), [expBreak]);
  const expPieData = useMemo(() => {
    if (expSorted.length <= PIE_TOP) return expSorted;
    const top = expSorted.slice(0, PIE_TOP);
    const otherAmount = expSorted.slice(PIE_TOP).reduce((s, c) => s + c.amount, 0);
    return [...top, { category: 'Other', amount: otherAmount }];
  }, [expSorted]);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <Title level={4} style={{ margin: 0, color: '#1a6b2a', display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 22, height: 22, backgroundColor: '#1a6b2a', WebkitMask: `url(${iconReport}) no-repeat center / contain`, mask: `url(${iconReport}) no-repeat center / contain`, flexShrink: 0 }} />
          Reports
        </Title>
        <Space wrap>
          <RangePicker
            picker="month"
            value={dateRange}
            onChange={setDateRange}
            allowClear
            placeholder={['From Month', 'To Month']}
            style={{ width: 240 }}
          />
          <Button icon={<ReloadOutlined />} onClick={fetchAll} />
        </Space>
      </div>

      <Row gutter={[16, 16]}>

        {/* ① Profit / Loss by Crop */}
        <Col xs={24} xl={15}>
          <ChartCard title="💰 Profit / Loss by Crop" subtitle="Expense · Income · Net profit per crop" loading={busy.crop}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={cropProfit} margin={{ top: 16, right: 8, left: 8, bottom: 48 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="crop_name" angle={-30} textAnchor="end" tick={{ fontSize: 12 }} interval={0} />
                <YAxis tickFormatter={shortInr} tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend verticalAlign="top" height={28} />
                <ReferenceLine y={0} stroke="#ccc" />
                <Bar dataKey="total_expense" name="Expense" fill={C.expense} radius={[4,4,0,0]} maxBarSize={28} />
                <Bar dataKey="total_income"  name="Income"  fill={C.income}  radius={[4,4,0,0]} maxBarSize={28} />
                <Bar dataKey="profit"        name="Profit"  radius={[4,4,0,0]} maxBarSize={28}>
                  {cropProfit.map((entry, i) => (
                    <Cell key={i} fill={entry.profit >= 0 ? C.profit : C.loss} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Col>

        {/* ② Expense Breakdown */}
        <Col xs={24} xl={9}>
          <ChartCard
            title="🧾 Where the money goes"
            loading={busy.exp}
          >
            {/* Filters */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
              <Select
                placeholder="Farm"
                options={farmOptions}
                value={expFilters.khetar_id}
                onChange={v => setExpFilters(f => ({ ...f, khetar_id: v }))}
                allowClear
                style={{ flex: '1 1 90px', minWidth: 90 }}
                size="small"
              />
              <Select
                placeholder="Pay status"
                options={PAYMENT_STATUS_OPTIONS}
                value={expFilters.payment_status}
                onChange={v => setExpFilters(f => ({ ...f, payment_status: v }))}
                allowClear
                style={{ flex: '1 1 100px', minWidth: 100 }}
                size="small"
              />
              <Select
                placeholder="Account"
                options={ACCOUNT_LINK_OPTIONS}
                value={expFilters.account_link}
                onChange={v => setExpFilters(f => ({ ...f, account_link: v }))}
                allowClear
                style={{ flex: '1 1 100px', minWidth: 100 }}
                size="small"
              />
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={expPieData} dataKey="amount" nameKey="category"
                  cx="50%" cy="50%" outerRadius={82} innerRadius={46}
                  paddingAngle={3}
                >
                  {expPieData.map((_, i) => (
                    <Cell key={i} fill={i < PIE_TOP ? C.pie[i % C.pie.length] : '#bdbdbd'} />
                  ))}
                </Pie>
                <Tooltip formatter={(v, n) => [inr(v), n]} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>

            {/* Scrollable category list */}
            <div style={{ maxHeight: 220, overflowY: 'auto', marginTop: 4, paddingRight: 4 }}>
              {expSorted.map((entry, i) => {
                const pct = expTotal > 0 ? ((entry.amount / expTotal) * 100).toFixed(1) : '0.0';
                const color = i < PIE_TOP ? C.pie[i % C.pie.length] : '#bdbdbd';
                return (
                  <div
                    key={i}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '5px 2px',
                      borderBottom: i < expSorted.length - 1 ? '1px solid #f0f0f0' : 'none',
                    }}
                  >
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
                    <span style={{ flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#333' }}>
                      {entry.category}
                    </span>
                    <span style={{ fontSize: 11, color: '#aaa', flexShrink: 0, minWidth: 36, textAlign: 'right' }}>
                      {pct}%
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 600, flexShrink: 0, minWidth: 64, textAlign: 'right', color: C.expense }}>
                      {inr(entry.amount)}
                    </span>
                  </div>
                );
              })}
            </div>
          </ChartCard>
        </Col>

        {/* ③ Income vs Expense Over Time */}
        <Col xs={24}>
          <ChartCard title="📅 Income vs Expense Over Time" subtitle="Monthly cash flow" loading={busy.flow}>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={cashflow} margin={{ top: 16, right: 24, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={shortInr} tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip labelFormatter={fmtMonth} />} />
                <Legend verticalAlign="top" height={28} />
                <Line type="monotone" dataKey="total_expense" name="Expense" stroke={C.expense} strokeWidth={2.5} dot={{ r: 4, fill: C.expense }} activeDot={{ r: 6 }} />
                <Line type="monotone" dataKey="total_income"  name="Income"  stroke={C.income}  strokeWidth={2.5} dot={{ r: 4, fill: C.income  }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Col>

        {/* ④ Year-over-Year Comparison */}
        <Col xs={24} lg={12}>
          <ChartCard title="📊 Year-over-Year Profit Comparison" subtitle="Net profit per crop by year" loading={busy.yoy}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={yoyData.data} margin={{ top: 16, right: 8, left: 8, bottom: 48 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="crop" angle={-30} textAnchor="end" tick={{ fontSize: 12 }} interval={0} />
                <YAxis tickFormatter={shortInr} tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend verticalAlign="top" height={28} />
                <ReferenceLine y={0} stroke="#ccc" />
                {yoyData.years.map((yr, i) => (
                  <Bar key={yr} dataKey={yr} name={yr} fill={C.yoy[i % C.yoy.length]} radius={[4,4,0,0]} maxBarSize={28} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Col>

        {/* ⑤ Plot Performance */}
        <Col xs={24} lg={12}>
          <ChartCard title="🗺️ Plot Performance" subtitle="Profit per unit area (bigha/acre)" loading={busy.plot}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={plotPerf} layout="vertical" margin={{ top: 8, right: 60, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                <XAxis type="number" tickFormatter={shortInr} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="plot_name" tick={{ fontSize: 12 }} width={90} />
                <Tooltip formatter={(v) => [inr(v), 'Profit / unit']} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <ReferenceLine x={0} stroke="#ccc" />
                <Bar dataKey="profit_per_unit_area" name="Profit/unit" radius={[0,4,4,0]} maxBarSize={22}>
                  <LabelList dataKey="profit_per_unit_area" position="right" formatter={shortInr} style={{ fontSize: 11, fill: '#555' }} />
                  {plotPerf.map((entry, i) => (
                    <Cell key={i} fill={entry.profit_per_unit_area >= 0 ? C.income : C.loss} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Col>

      </Row>
    </div>
  );
}
